import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrg } from '../../hooks/useOrg';
import { useNotifications } from '../NotificationSystem';
import { auditThemeService } from '../../services/auditThemeService';
import '../qms/AuditQMSDetail.css';

const AuditThemesManager = () => {
  const { t } = useTranslation(['qms', 'common']);
  const { notify } = useNotifications();
  const { activeOrgId } = useOrg();
  const [tab, setTab] = useState('active');
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [newCategory, setNewCategory] = useState('');

  const load = async () => {
    if (!activeOrgId) {
      setThemes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await auditThemeService.getAllThemes(activeOrgId);
      setThemes(list);
      const cats = [...new Set(list.map((x) => x.category).filter(Boolean))];
      if (cats.length && !newCategory) setNewCategory(cats[0]);
    } catch (e) {
      console.error(e);
      notify.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrgId]);

  const categories = [...new Set(themes.map((x) => x.category).filter(Boolean))];

  const activeList = themes.filter((x) => x.isActive);
  const inactiveList = themes.filter((x) => !x.isActive);

  const addTheme = async () => {
    if (!newLabel.trim()) {
      notify.error(t('qms:themeLabelRequired'));
      return;
    }
    try {
      await auditThemeService.addTheme(activeOrgId, {
        label: newLabel.trim(),
        category: newCategory || 'General'
      });
      setNewLabel('');
      notify.success(t('qms:themeAdded'));
      await load();
    } catch (e) {
      notify.error(e.message);
    }
  };

  const deactivate = async (id, isDefault) => {
    if (isDefault) return;
    try {
      await auditThemeService.deactivateTheme(activeOrgId, id);
      await load();
    } catch (e) {
      notify.error(e.message);
    }
  };

  const activate = async (id) => {
    try {
      await auditThemeService.activateTheme(activeOrgId, id);
      await load();
    } catch (e) {
      notify.error(e.message);
    }
  };

  if (!activeOrgId) {
    return <div className="aqd-loading">{t('qms:noOrganizationContext')}</div>;
  }

  if (loading) {
    return <div className="aqd-loading">{t('common:loading')}</div>;
  }

  const list = tab === 'active' ? activeList : inactiveList;

  return (
    <div className="audit-qms-detail" style={{ padding: '1rem' }}>
      <h2 style={{ marginBottom: '1rem' }}>{t('qms:auditThemesManager')}</h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          type="button"
          className={`btn btn-sm ${tab === 'active' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('active')}
        >
          {t('qms:activeThemes')}
        </button>
        <button
          type="button"
          className={`btn btn-sm ${tab === 'inactive' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('inactive')}
        >
          {t('qms:inactiveThemes')}
        </button>
      </div>

      <div className="aqd-form-grid" style={{ marginBottom: 24, maxWidth: 560 }}>
        <div className="form-field">
          <label className="form-label">{t('qms:themeNewLabel')}</label>
          <input className="form-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-label">{t('qms:themeCategory')}</label>
          <select className="form-input" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            {!categories.includes('General') && <option value="General">General</option>}
          </select>
        </div>
        <div className="form-field" style={{ alignSelf: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={addTheme}>
            {t('common:add')}
          </button>
        </div>
      </div>

      <ul style={{ listStyle: 'none', padding: 0 }}>
        {list.map((th) => (
          <li
            key={th.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              marginBottom: 8
            }}
          >
            <div>
              <strong>{th.label}</strong>
              <span style={{ marginLeft: 8, color: '#6b7280' }}>{th.category}</span>
              {th.isDefault && (
                <span style={{ marginLeft: 8, fontSize: 12, color: '#7c3aed' }}>{t('qms:defaultTheme')}</span>
              )}
            </div>
            {tab === 'active' ? (
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                disabled={th.isDefault}
                title={th.isDefault ? t('qms:cannotDeactivateDefault') : ''}
                onClick={() => deactivate(th.id, th.isDefault)}
              >
                {t('qms:deactivate')}
              </button>
            ) : (
              <button type="button" className="btn btn-sm btn-primary" onClick={() => activate(th.id)}>
                {t('qms:activate')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AuditThemesManager;
